import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, message, Dropdown, Modal } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  LinkOutlined,
  MoreOutlined,
  StarOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DragOutlined,
  DeleteOutlined,
  FundProjectionScreenOutlined,
  StarFilled,
  ArrowLeftOutlined,
  CommentOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { CodeNode } from '@lexical/code';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import ToolbarPlugin from './ToolbarPlugin';
import './Layout.css';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  MentionNode,
  LayoutNode,
  UploadNode,
  LayoutColumnNode,
  DateNode,
  StatusNode,
  CodeBlockNode,
  PanelNode,
  DividerNode,
  CheckboxNode,
} from './CustomNode';
import BlockExitPlugin from './BlockExitPlugin';
import BlockControlsPlugin from './BlockControlsPlugin';
import DragDropPlugin from './DragDropPlugin';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSpaceAssigneeList } from 'store/actions';
import DeleteBlockPlugin from './DeleteBlockPlugin';
import { useQueryClient } from '@tanstack/react-query';
import MoveModal from '../Sidebar/MoveModal';
import DuplicateModal from '../Sidebar/DuplicateModal';
import PageComments from './PageComments';
import FloatingCommentPlugin from './FloatingCommentPlugin';
import ShareItemPermision from '../share/ShareItemPermision';
import UserAvatar from 'components/userAvatar';
import { CommentMarkNode } from './CommentMarkNode';
import CommentsPanel from './CommentsPanel';
import SkeletonWithLoaders from 'components/common/Spinner';
import MentionPlugin from './MentionPlugin';
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $getRoot,
} from 'lexical';
import Cookies from 'js-cookie';

const theme = {
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
  },
  table: 'lexical-table',
  tableCell: 'lexical-table-cell',
  tableRow: 'lexical-table-row',
  layout: 'editor-layout',
  layoutColumn: 'editor-layout-column',
};

const initialConfig = {
  namespace: 'ConfluenceCloneEditor',
  theme,
  onError(error) {
    console.error('Lexical Error:', error);
  },
  nodes: [
    HeadingNode,
    QuoteNode,
    CodeNode,
    ListNode,
    ListItemNode,
    LinkNode,
    AutoLinkNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    MentionNode,
    LayoutNode,
    UploadNode,
    LayoutColumnNode,
    DateNode,
    StatusNode,
    CodeBlockNode,
    PanelNode,
    DividerNode,
    CheckboxNode,
    CommentMarkNode,
  ],
};

function EditorRefPlugin({ editorRef }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  return null;
}

const PageContent = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [initialEditorState, setInitialEditorState] = useState(null);
  const [author, setAuthor] = useState('');
  const [editorJson, setEditorJson] = useState(null);
  const [lastSavedJson, setLastSavedJson] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemType, setItemType] = useState(null);

  const [moveOpen, setMoveOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [selectedDuplicateNode, setSelectedDuplicateNode] = useState(null);

  const [pageStatus, setPageStatus] = useState('draft');
  const [isStarred, setIsStarred] = useState(false);

  const [previewMode, setPreviewMode] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(pageStatus !== 'published');
  const [saveStatus, setSaveStatus] = useState('idle');

  const [isCommentsOpen, setIsCommentsOpen] = useState(false);

  const roleId = Number(Cookies.get('space-role-id'));

  const isAdmin = roleId === 1;

  const isViewer = roleId === 5;

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const navigate = useNavigate();

  const editorRef = useRef(null);
  const queryClient = useQueryClient();
  const editorContentRef = useRef(null);

  const deleteAttachmentRef = useRef(null);

  const spaceUsers = useSelector(
    (state) => state.Authentication?.spaceassignees,
  );

  const { spaceid, pageid } = useParams();

  const handleChange = (editorState) => {
    const json = editorState.toJSON();
    setEditorJson(json);
  };

  useEffect(() => {
    if (!editorRef.current || !initialEditorState) return;

    const editor = editorRef.current;

    editor.update(() => {
      const newState = editor.parseEditorState(initialEditorState);
      editor.setEditorState(newState);
    });
  }, [initialEditorState, pageid]);

  const HandleEditContent = async () => {
    try {
      if (!editorJson) {
        toast.message('Nothing to save');
        return;
      }

      // setSaveStatus('saving');

      const payload = {
        space_id: Number(spaceid),
        item_id: Number(pageid),
        title: title,
        content: editorJson,
      };
      const response = await httpInjectorService.postPageContent(payload);
      if (response.status === 'success') {
        // setSaveStatus('saved');
        toast.success('Version upgrated successfully');
        await GetPageContent();
        await getAttachment(editorRef.current);
      }

      setTimeout(() => {
        setSaveStatus('idle');
        if (pageStatus === 'published') {
          setIsEditing(false);
        }
      }, 1500);
      queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
      //   console.log(response);
    } catch (error) {
      // toast.error(error.message);
      console.error(error);
      // setSaveStatus('idle');
    }
  };

  const GetPageContent = async () => {
    setLoading(true);
    try {
      const response = await httpInjectorService.getPageContent(
        spaceid,
        pageid,
      );
      const data = response?.data;

      setTitle(data.title || 'Untitled');
      setAuthor(data.username);
      setPageStatus(data.status);
      setIsStarred(data.is_stared === 1);
      setItemType(data.item_type);

      // if (!editorRef.current) return;

      // const editor = editorRef.current;

      let content = data.content;

      // empty editor state
      if (!content) {
        content = JSON.stringify({
          root: {
            children: [
              {
                type: 'paragraph',
                version: 1,
                indent: 0,
                direction: null,
                format: '',
                children: [],
              },
            ],
            direction: null,
            format: '',
            indent: 0,
            type: 'root',
            version: 1,
          },
        });
      }

      // if (!content) {
      //   console.warn('No content received, skipping overwrite');
      //   return;
      // }

      setInitialEditorState(content);
      // const newState = editor.parseEditorState(content);
      // editor.setEditorState(newState);

      setPageContent(content);
      setLastSavedJson(content);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveData = async () => {
    try {
      if (!editorJson) {
        toast.message('Nothing to save');
        return;
      }

      setSaveStatus('saving');

      const payload = {
        item_id: Number(pageid),
        title: title,
        content: editorJson,
      };
      const response = await httpInjectorService.savePageData(payload);
      if (response.status === 'success') {
        setSaveStatus('saved');
        toast.success('PageContent saved successfully');
        await GetPageContent();
        await getAttachment(editorRef.current);
      }

      setTimeout(() => {
        setSaveStatus('idle');
        if (pageStatus === 'published') {
          setIsEditing(false);
        }
      }, 1500);
      queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
    } catch (error) {
      toast.error(error.message);
      console.error(error);
      setSaveStatus('idle');
    }
  };

  const handleTitleUpdate = async (newTitle) => {
    try {
      await httpInjectorService.renameContent({
        space_id: Number(spaceid),
        item_id: Number(pageid),
        title: newTitle,
        item_type: itemType,
      });

      queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePagePublish = async () => {
    try {
      const payload = {
        space_id: Number(spaceid),
        item_id: Number(pageid),
        item_type: itemType,
      };
      const response = await httpInjectorService.publishPage(payload);
      if (response.status === 'success') {
        await GetPageContent();
        queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
        toast.success(response.message || 'Page published succesfully', {
          autoClose: 1000,
          position: 'top-right',
        });
      }
    } catch (error) {
      toast.error(error.message, {
        autoClose: 1000,
        position: 'top-right',
      });
    }
  };

  useEffect(() => {
    if (spaceid && pageid) {
      GetPageContent();
    }
  }, [spaceid, pageid]);

  useEffect(() => {
    if (pageStatus === 'published') {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [pageStatus]);

  // useEffect(() => {
  //   if (!editorJson) return;
  //   const timeout = setTimeout(() => {
  //     if (JSON.stringify(editorJson) !== JSON.stringify(lastSavedJson)) {
  //       HandleEditContent();
  //       setLastSavedJson(editorJson);
  //     }
  //   }, 8000);
  //   return () => clearTimeout(timeout);
  // }, [editorJson]);

  const handleDuplicate = () => {
    setSelectedDuplicateNode({
      item_id: pageid,
      item_type: itemType,
    });
    setDuplicateOpen(true);
  };

  const handleStarContent = async () => {
    try {
      const payload = {
        space_id: Number(spaceid),
        item_id: Number(pageid),
        item_type: itemType,
        action: 'star',
      };
      const response = await httpInjectorService.starShortcut(payload);
      // console.log(response);
      if (response.status === 'success') {
        toast.success(response.message, {
          autoClose: 1000,
          position: 'top-right',
        });
        setIsStarred(!isStarred);
      }
    } catch (error) {
      toast.error(error.message, {
        autoClose: 1000,
        position: 'top-right',
      });
    }
  };

  const viewVersionHistory = async () => {
    navigate('version-history', {
      state: {
        item_type: itemType,
      },
    });
  };

  const handleDuplicateConfirm = async ({
    original_id,
    target_space_id,
    parent_id,
    prefix,
    is_child,
  }) => {
    await httpInjectorService.duplicateContent({
      space_id: Number(target_space_id),
      item_id: Number(pageid),
      parent_id: parent_id || null,
      startname: prefix,
      is_child: 'no',
    });

    queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
  };

  const handleMove = async ({ dragId, dropId }) => {
    await httpInjectorService.movePageOrFolders({
      space_id: Number(spaceid),
      item_id: Number(pageid),
      parent_id: dropId,
    });
    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleDelete = async () => {
    const response = await httpInjectorService.deleteorArchivePgorFolder({
      space_id: Number(spaceid),
      item_id: pageid,
      status_type: 'deleted',
      nested_item: 'yes',
      type: itemType,
    });
    if (response.status === 'success') {
      setDeleteModalOpen(false);
      toast.success('Item Deleted successfully');
      navigate(`${window.location.origin}/admin/confluence/space/${spaceid}`);
    }

    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleArchive = async () => {
    const response = await httpInjectorService.deleteorArchivePgorFolder({
      space_id: Number(spaceid),
      item_id: pageid,
      status_type: 'archived',
      nested_item: 'yes',
      type: itemType,
    });
    if (response.status === 'success') {
      setDeleteModalOpen(false);
      toast.success('Item Archives successfully');
      navigate(`${window.location.origin}/admin/confluence/space/${spaceid}`);
    }

    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const deleteDraftPage = async () => {
    try {
      const response = await httpInjectorService.deleteDraftPage(
        spaceid,
        pageid,
      );

      if (response.status === 'success') {
        toast.success('Item Deleted successfully');
        navigate(`${window.location.origin}/admin/confluence/space/${spaceid}`);
      }

      queryClient.invalidateQueries(['spaceContent', spaceid]);
    } catch (error) {
      console.error('Delete draft failed', error);
    }
  };

  const handleAddAttachment = async (file, inlinePosition, mimeType) => {
    try {
      const formData = new FormData();
      formData.append('space_id', spaceid);
      formData.append('item_id', pageid);
      formData.append('file', file);
      formData.append('inline_position', JSON.stringify(inlinePosition));
      formData.append('mime_type', mimeType || '');

      const res = await httpInjectorService.postSpaceAttachment(formData);
      if (res.status === 'success') {
        await getAttachment(editorRef.current);
      }
      return { ...res?.data, mimeType };
    } catch (err) {
      console.error(err);
    }
  };

  const getAttachment = async (editor) => {
    try {
      if (!editor) editor = editorRef.current;
      if (!editor) return;

      const response = await httpInjectorService.getSpaceAttachments(
        spaceid,
        pageid,
      );
      const attachments = response?.data || [];
      // console.log(
      //   'attachments:',
      //   attachments.map((a) => ({
      //     name: a.attachment_name,
      //     mime: a.mime_type,
      //     url: a.attachment_url,
      //   })),
      // );

      editor.update(() => {
        const root = $getRoot();
        const removeUploads = (node) => {
          const children = node.getChildren?.();
          if (!children) return;
          for (const child of [...children]) {
            if (child.getType?.() === 'upload') child.remove();
            else removeUploads(child);
          }
        };
        removeUploads(root);
      });

      await new Promise((r) => setTimeout(r, 50));

      const processed = await Promise.all(
        attachments.map(async (att) => {
          let position = att.inline_position;
          if (typeof position === 'string') {
            try {
              position = JSON.parse(position);
            } catch {
              position = null;
            }
          }

          const isImage = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = att.attachment_url;
          });

          return { att, position, isImage };
        }),
      );

      editor.update(() => {
        processed.forEach(({ att, position, isImage }) => {
          const uploadNode = new UploadNode(
            att.attachment_name,
            att.attachment_url,
            isImage,
            att.id,
            att.mime_type || '',
          );
          if (position?.block_id) {
            const targetNode = $getNodeByKey(position.block_id);
            if (targetNode) {
              targetNode.append(uploadNode);
              return;
            }
          }
          $getRoot().append(uploadNode);
        });
      });
    } catch (error) {
      console.error('Error fetching attachments', error);
    }
  };

  const deleteAttachment = async (attachmentId, editor) => {
    try {
      await httpInjectorService.deleteSpaceAttachment(
        spaceid,
        pageid,
        attachmentId,
      );

      editor.update(() => {
        const root = $getRoot();

        const removeUploadNode = (node) => {
          const children = node.getChildren?.();
          if (!children) return;

          for (const child of children) {
            if (
              child.getType?.() === 'upload' &&
              child.__attachmentId === attachmentId
            ) {
              child.remove();
              return;
            }
            removeUploadNode(child);
          }
        };

        removeUploadNode(root);
      });
    } catch (error) {
      console.error('Delete failed', error);
    }
  };

  useEffect(() => {
    deleteAttachmentRef.current = deleteAttachment;
  });

  useEffect(() => {
    window.__deleteAttachment = (attachmentId) => {
      // console.log('window.__deleteAttachment called with:', attachmentId);
      deleteAttachmentRef.current?.(attachmentId, editorRef.current);
    };
    return () => {
      delete window.__deleteAttachment;
    };
  }, []);

  useEffect(() => {
    if (editorRef.current && pageid && spaceid) {
      getAttachment(editorRef.current);
    }
  }, [pageid, spaceid]);

  const handleCloseComment = () => {
    setIsCommentsOpen(false);
  };

  useEffect(() => {
    if (!previewMode && pageContent && editorRef.current) {
      const editor = editorRef.current;

      const parsedState = editor.parseEditorState(pageContent);
      editor.setEditorState(parsedState);
    }
  }, [previewMode]);

  const handleExport = async (format) => {
    const element = editorContentRef.current;
    if (!element) {
      toast.error('Nothing to export');
      return;
    }

    const cloned = element.cloneNode(true);
    const selectorsToRemove = [
      '.page-comments',
      '.comment-mark',
      '.floating-comment',
      '[data-comment]',
      '.comment-highlight',
      '.comments-panel',
      '.floating-comment-btn',
    ];
    selectorsToRemove.forEach((selector) => {
      cloned.querySelectorAll(selector).forEach((el) => el.remove());
    });

    if (format === 'pdf') {
      const html2pdf = (await import('html2pdf.js')).default;

      const options = {
        margin: [15, 15, 15, 15],
        filename: `${title || 'page'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
      };

      toast.info('Generating PDF...', { autoClose: 2000 });
      html2pdf().set(options).from(cloned).save();
    }

    if (format === 'docx') {
      const {
        Document,
        Packer,
        Paragraph,
        TextRun,
        HeadingLevel,
        Table,
        TableRow,
        TableCell,
        WidthType,
        BorderStyle,
      } = await import('docx');

      const paragraphs = [];

      const parseNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) {
            return [new Paragraph({ children: [new TextRun(text)] })];
          }
          return [];
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return [];

        const tag = node.tagName?.toLowerCase();
        const children = Array.from(node.childNodes);

        if (['h1', 'h2', 'h3', 'h4'].includes(tag)) {
          const level = {
            h1: HeadingLevel.HEADING_1,
            h2: HeadingLevel.HEADING_2,
            h3: HeadingLevel.HEADING_3,
            h4: HeadingLevel.HEADING_4,
          }[tag];
          return [
            new Paragraph({
              text: node.textContent?.trim(),
              heading: level,
            }),
          ];
        }

        if (tag === 'p') {
          const runs = [];
          children.forEach((child) => {
            const text = child.textContent?.trim();
            if (!text) return;
            const isBold = child.tagName === 'STRONG' || child.tagName === 'B';
            const isItalic = child.tagName === 'EM' || child.tagName === 'I';
            const isUnderline = child.tagName === 'U';
            runs.push(
              new TextRun({
                text,
                bold: isBold,
                italics: isItalic,
                underline: isUnderline ? {} : undefined,
              }),
            );
          });
          if (runs.length === 0 && node.textContent?.trim()) {
            runs.push(new TextRun(node.textContent.trim()));
          }
          return runs.length > 0
            ? [new Paragraph({ children: runs })]
            : [new Paragraph({})]; // empty line
        }

        if (tag === 'ul' || tag === 'ol') {
          const items = [];
          node.querySelectorAll('li').forEach((li) => {
            items.push(
              new Paragraph({
                text: li.textContent?.trim(),
                bullet: tag === 'ul' ? { level: 0 } : undefined,
                numbering:
                  tag === 'ol'
                    ? { reference: 'default-numbering', level: 0 }
                    : undefined,
              }),
            );
          });
          return items;
        }

        if (tag === 'table') {
          const rows = [];
          node.querySelectorAll('tr').forEach((tr) => {
            const cells = [];
            tr.querySelectorAll('td, th').forEach((td) => {
              cells.push(
                new TableCell({
                  children: [
                    new Paragraph({ text: td.textContent?.trim() || '' }),
                  ],
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                  },
                }),
              );
            });
            if (cells.length > 0) rows.push(new TableRow({ children: cells }));
          });
          if (rows.length > 0) {
            return [
              new Table({
                rows,
                width: { size: 100, type: WidthType.PERCENTAGE },
              }),
            ];
          }
          return [];
        }

        if (tag === 'blockquote' || tag === 'pre' || tag === 'code') {
          return [
            new Paragraph({
              children: [
                new TextRun({
                  text: node.textContent?.trim(),
                  font: 'Courier New',
                  size: 20,
                }),
              ],
            }),
          ];
        }

        if (tag === 'hr') {
          return [new Paragraph({ text: '─'.repeat(50) })];
        }

        const nested = [];
        children.forEach((child) => {
          nested.push(...parseNode(child));
        });
        return nested;
      };

      paragraphs.push(
        new Paragraph({
          text: title || 'Untitled',
          heading: HeadingLevel.TITLE,
        }),
      );

      if (author) {
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `By ${author}`,
                italics: true,
                color: '666666',
              }),
            ],
          }),
        );
        paragraphs.push(new Paragraph({}));
      }

      Array.from(cloned.childNodes).forEach((child) => {
        paragraphs.push(...parseNode(child));
      });

      const doc = new Document({
        numbering: {
          config: [
            {
              reference: 'default-numbering',
              levels: [
                {
                  level: 0,
                  format: 'decimal',
                  text: '%1.',
                  alignment: 'left',
                },
              ],
            },
          ],
        },
        sections: [
          {
            properties: {},
            children: paragraphs,
          },
        ],
      });

      toast.success('Generating Word document...', { autoClose: 2000 });
      const buffer = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(buffer);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'page'}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  const moreMenu = {
    items: [
      {
        key: 'star',
        icon: isStarred ? (
          <StarFilled style={{ color: '#faad14' }} />
        ) : (
          <StarOutlined />
        ),
        label: isStarred ? 'Unstar' : 'Star',
        hidden: pageStatus === 'draft',
      },
      {
        key: 'preview',
        icon: <FundProjectionScreenOutlined />,
        label: 'Preview',
      },
      {
        key: 'export',
        icon: <ExportOutlined />,
        label: 'Export',
        children: [
          { key: 'export_pdf', label: 'Export as PDF' },
          { key: 'export_docx', label: 'Export as Word' },
        ],
      },
      {
        key: 'version',
        icon: <ClockCircleOutlined />,
        label: 'Version History',
        hidden: pageStatus === 'draft',
      },
      {
        key: 'duplicate',
        icon: <CopyOutlined />,
        label: 'Duplicate',
        hidden: pageStatus === 'draft',
      },
      {
        key: 'move',
        icon: <DragOutlined />,
        label: 'Move',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Archive and Delete',
      },
    ],

    onClick: ({ key }) => {
      if (key === 'version') viewVersionHistory();

      if (key === 'star') handleStarContent();

      if (key === 'duplicate') handleDuplicate();

      if (key === 'move') setMoveOpen(true);

      if (key === 'preview') setPreviewMode(true);

      if (key === 'export_pdf') handleExport('pdf');
      if (key === 'export_docx') handleExport('docx');

      if (key === 'delete') setDeleteModalOpen(true);
    },
  };

  if (previewMode) {
    return (
      <div style={{ padding: 40, maxWidth: 900, margin: '0 auto' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => setPreviewMode(false)}
          style={{ marginBottom: 20 }}
        >
          Back
        </Button>

        <h1
          style={{
            fontSize: 36,
            fontWeight: 700,
            marginBottom: 8,
          }}
        >
          {title}
        </h1>

        <div style={{ color: '#666', marginBottom: 20 }}>By {author}</div>

        <LexicalComposer
          initialConfig={{
            ...initialConfig,
            editable: false,
            editorState: (editor) => {
              if (pageContent) {
                const parsedState = editor.parseEditorState(pageContent);
                editor.setEditorState(parsedState);
              }
            },
          }}
        >
          <RichTextPlugin
            contentEditable={<ContentEditable />}
            placeholder={null}
          />
        </LexicalComposer>
      </div>
    );
  }

  if (loading || !initialEditorState) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SkeletonWithLoaders size="large" />
      </div>
    );
  }

  return (
    <>
      <LexicalComposer
        key={`editor-${pageid}-${previewMode}`}
        initialConfig={initialConfig}
      >
        <div className="page-wrapper">
          <div className="editor-card">
            <div className="editor-card-header">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                bordered={false}
                className="editor-title-input"
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  width: 400,
                }}
              />

              <div className="editor-actions">
                {pageStatus === 'published' && !isEditing ? (
                  <>
                    {!isViewer && (
                      <Button
                        style={{ backgroundColor: '#722ed1', color: 'white' }}
                        onClick={() => setIsEditing(true)}
                      >
                        Edit
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        style={{ backgroundColor: '#722ed1', color: 'white' }}
                        onClick={HandleEditContent}
                      >
                        Upgrade
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    onClick={handleSaveData}
                    loading={saveStatus === 'saving'}
                  >
                    {saveStatus === 'saved' ? 'Saved' : 'Save'}
                  </Button>
                )}

                {isAdmin &&
                  (pageStatus === 'published' ? (
                    <span
                      style={{
                        background: '#e6f4ea',
                        color: '#1e7e34',
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      Published
                    </span>
                  ) : (
                    <Button
                      onClick={handlePagePublish}
                      style={{ backgroundColor: '#722ed1', color: 'white' }}
                    >
                      Publish
                    </Button>
                  ))}
                {!isViewer && (
                  <Button icon={<LinkOutlined />} onClick={onOpen}>
                    Share
                  </Button>
                )}
                {isAdmin && (
                  <Dropdown menu={moreMenu} trigger={['click']}>
                    <Button icon={<MoreOutlined />} />
                  </Dropdown>
                )}
              </div>
            </div>

            {isEditing && !isViewer && (
              <div className="editor-toolbar-wrapper">
                <ToolbarPlugin onUpload={handleAddAttachment} />
              </div>
            )}

            <div className="editor-content-wrapper">
              <div className="editor-split-container">
                <div
                  className={`editor-left ${isCommentsOpen ? 'shrink' : ''}`}
                >
                  <div className="editor-content" ref={editorContentRef}>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      onBlur={(e) => handleTitleUpdate(e.target.value)}
                      placeholder="Give this Page a Title"
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        fontSize: 32,
                        fontWeight: 700,
                        marginBottom: 8,
                        marginTop: 15,
                      }}
                    />

                    <div
                      style={{
                        color: '#666',
                        fontSize: 14,
                        marginBottom: 24,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <UserAvatar username={author} size={24} />
                        By<span>{author}</span>
                      </div>
                    </div>
                    {/* EDITOR */}
                    <div className="editor-body">
                      <RichTextPlugin
                        contentEditable={
                          <ContentEditable
                            className="editor-input-area"
                            contentEditable={isEditing}
                          />
                        }
                        placeholder={
                          <div className="editor-placeholder">
                            Type / for commands
                          </div>
                        }
                      />
                      <EditorRefPlugin editorRef={editorRef} />

                      {isEditing && (
                        <>
                          <BlockControlsPlugin onUpload={handleAddAttachment} />
                          <DeleteBlockPlugin />
                          <BlockExitPlugin />
                          <HistoryPlugin />
                          <DragDropPlugin />
                          <ListPlugin />
                          <LinkPlugin />
                          <TablePlugin />
                          <FloatingCommentPlugin
                            pageid={pageid}
                            spaceid={spaceid}
                            HandleEditContent={HandleEditContent}
                          />
                          <MentionPlugin users={spaceUsers} />
                          <OnChangePlugin onChange={handleChange} />
                        </>
                      )}
                      {pageStatus === 'published' && (
                        <PageComments pageid={pageid} spaceid={spaceid} />
                      )}
                    </div>
                  </div>
                </div>
                {isCommentsOpen && (
                  <div className="editor-right">
                    <CommentsPanel
                      pageid={pageid}
                      spaceid={spaceid}
                      onClose={handleCloseComment}
                    />
                  </div>
                )}
              </div>
            </div>

            <Button
              className="floating-comment-btn"
              shape="circle"
              icon={<CommentOutlined />}
              size="large"
              onClick={() => setIsCommentsOpen((prev) => !prev)}
            />
          </div>
        </div>
      </LexicalComposer>

      <MoveModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        node={{
          item_id: pageid,
        }}
        spaceid={spaceid}
        onConfirm={handleMove}
      />

      <DuplicateModal
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        originalNode={selectedDuplicateNode}
        spaceid={spaceid}
        onConfirm={handleDuplicateConfirm}
      />

      <ShareItemPermision
        isOpen={isOpen}
        onClose={onClose}
        space_id={spaceid}
        item_id={pageid}
      />

      <Modal
        open={deleteModalOpen}
        footer={null}
        onCancel={() => setDeleteModalOpen(false)}
      >
        {pageStatus === 'draft' ? (
          <>
            <h3>Delete Draft Page</h3>

            <p>
              This is a draft page. Drafts cannot be archived and will be
              permanently deleted.
            </p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '20px',
              }}
            >
              <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>

              <Button danger onClick={deleteDraftPage}>
                Delete Permanently
              </Button>
            </div>
          </>
        ) : (
          <>
            <h3>Archive or Delete Page</h3>

            <p>
              Items in the trash can be restored or purged by a space admin. If
              you prefer, you can archive this instead.
            </p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '20px',
              }}
            >
              <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>

              <Button onClick={handleArchive}>Archive</Button>

              <Button danger onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default PageContent;
